// Generated by tutorials/record.cjs from tutorials/articles/h37-credits.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h37 --text-only` (wording) or
// `node tutorials/record.cjs h37` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'credits',
  catalogId: 'h37',
  title: 'Meal credits and End of Month',
  summary: 'Some facilities give residents a number of meal credits each month and charge for the meals they buy. At the end of the month, the Credits page posts every resident\'s month: meals use their credits while they last, the rest is billed, and residents who were away get a refund.',
  audience: 'Dietary managers and business office staff at facilities that charge residents for meals',
  area: 'residents',
  learn: [
    'What meal credits are, and how a facility switches them on',
    'Open a month on the Credits page and read what it will post',
    'Review one resident\'s month and change which meals use a credit',
    'Post the month for every resident at once',
    'What posting changes, and the attendance refund',
  ],
  keywords: [
    'credits',
    'credit',
    'meal credits',
    'end of month',
    'end-of-month',
    'post credits',
    'posting',
    'credit process',
    'billing',
    'bill to account',
    'meal charges',
    'charge',
    'refund',
    'attendance refund',
    'days away',
    'not present',
    'auto refill',
    'use credits',
    'review',
    'month end',
    'meal plan',
  ],
  steps: [
    {
      id: 'switch-on',
      part: 'How credits work',
      heading: 'Credits are switched on per facility',
      text: 'Credits are for facilities that sell meals to residents against a monthly allowance. Each resident gets a number of credits a month for each credit meal, and a meal they buy uses one.\n\nYour administrator switches them on in Setup, Facility Settings, Credits / Costs: Use Credits, then the Mealtime boxes for the meals that use credits. Auto Refill On First Of Month gives residents their new credits each month. Not Present Refund sets the days away that earn a refund, and the refund per day. Meal prices are on the Costs tab.',
      media: {
        image: {
          src: '/help/credits/switch-on.webp',
          width: 1280,
          height: 970,
        },
        clip: {
          src: '/help/credits/switch-on.mp4',
          width: 1280,
          height: 970,
          durationSec: 3,
        },
        alt: 'Facility Settings, Credits / Costs: Use Credits and Auto Refill On First Of Month checked, Lunch and Dinner checked under Mealtime, and Not Present Refund at 5 days and $12.00 a day.',
      },
    },
    {
      id: 'refund',
      part: 'How credits work',
      heading: 'The attendance refund',
      text: 'Under Not Present Refund, Days Not Present Per Month is how many days a resident must be away in a month to get a refund, and Refund Per Day what they get back for each day. 0 days turns the refund off.\n\nThe refund is added when the resident\'s month is posted.',
      media: {
        image: {
          src: '/help/credits/refund.webp',
          width: 792,
          height: 258,
        },
        alt: 'The Not Present Refund box: Days Not Present Per Month 5 and Refund Per Day $12.00.',
      },
    },
    {
      id: 'month',
      part: 'How credits work',
      heading: 'Through the month, then at the end',
      text: 'Through the month, each meal a resident buys at a credit mealtime, for example ordered in Selective Touch, is recorded as a charge on their record, under Credits.\n\nOnce the month has ended, it is posted on the Credits page: each charged meal uses one of the resident\'s credits while they last, and whatever is left is billed to the resident\'s account.',
      textOnly: true,
    },
    {
      id: 'open',
      part: 'Open the month',
      heading: 'Open Credits',
      text: 'In the menu, select Residents, then Credits. The page opens on the last month that has ended; pick another in Month. The month you are in is not offered, because it is still being charged.',
      media: {
        image: {
          src: '/help/credits/open.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/credits/open.mp4',
          width: 1280,
          height: 800,
          durationSec: 5,
        },
        alt: 'The Credits page for October 2026, with the Month box, the Post 7 residents button, the totals and the list of residents.',
      },
    },
    {
      id: 'totals',
      part: 'Open the month',
      heading: 'The month at a glance',
      text: 'The boxes at the top add up the month: Residents to post, Meals using a credit, To bill (the meals left over once credits run out), the Attendance refunds due, and the residents Already posted.',
      media: {
        image: {
          src: '/help/credits/totals.webp',
          width: 1280,
          height: 122,
        },
        alt: 'The totals: 7 Residents to post, 262 Meals using a credit, $215.50 To bill, Attendance refunds $84.00 for 1 resident, and 2 Already posted.',
      },
    },
    {
      id: 'list',
      part: 'Open the month',
      heading: 'Each resident\'s month',
      text: 'Each row is a resident with meals charged in the month. Meals to post counts their charges, Using a credit how many their credits cover, and To bill what the rest comes to. Credits go to the most expensive meals first.\n\nDays away and Refund show when the resident was away long enough for an attendance refund. A resident discharged since is still listed, tagged Discharged, so their month is posted too. Status says To post or Posted; posted residents are greyed out.',
      media: {
        image: {
          src: '/help/credits/list.webp',
          width: 1280,
          height: 720,
        },
        alt: 'The list: Achebe, Raymond with 52 meals to post, 40 using a credit and $107.75 to bill; Marsh, Theodore 7 days away with an $84.00 refund; Novak, Stanley tagged Discharged; Lindqvist and Pembroke greyed out as Posted.',
      },
    },
    {
      id: 'review',
      part: 'Review one resident',
      heading: 'Open Review',
      text: 'Select Review on a resident\'s row to see their month meal by meal before it is posted.\n\nAt the top, Credits Available says how many credits are still free at each credit meal, and Balance To Be Billed what the meals without a credit come to. Each row is one meal: when it was charged, the meal, its cost and a note. A check in Use Credit means the meal uses a credit; the others are billed.',
      media: {
        image: {
          src: '/help/credits/review.webp',
          width: 1280,
          height: 642,
        },
        clip: {
          src: '/help/credits/review.mp4',
          width: 1280,
          height: 642,
          durationSec: 2,
        },
        alt: 'Credits - End Of Month for Raman, Priya (October 2026): Credits Available L: 0 D: 0, Balance To Be Billed $43.75, and her meals with Use Credit checked on most of them.',
      },
    },
    {
      id: 'change',
      part: 'Review one resident',
      heading: 'Change which meals use a credit',
      text: 'Clear a meal\'s Use Credit box to bill it instead. Its credit goes back to Credits Available, and Balance To Be Billed goes up by its cost. Check another meal to give it that credit. A meal can only be checked while a credit is free for its mealtime.',
      media: {
        image: {
          src: '/help/credits/change.webp',
          width: 1280,
          height: 642,
        },
        clip: {
          src: '/help/credits/change.mp4',
          width: 1280,
          height: 642,
          durationSec: 3.1,
        },
        alt: 'A $12.75 lunch with Use Credit cleared: Credits Available now L: 1 and Balance To Be Billed $56.50.',
      },
    },
    {
      id: 'confirm',
      part: 'Review one resident',
      heading: 'Confirm',
      text: 'Select Confirm, then Yes. If a credit is still free, Dietech asks first whether to post without using it.\n\nThe resident\'s month is posted: their row says Posted.',
      media: {
        image: {
          src: '/help/credits/confirm.webp',
          width: 708,
          height: 300,
        },
        clip: {
          src: '/help/credits/confirm.mp4',
          width: 708,
          height: 300,
          durationSec: 2.7,
        },
        alt: 'Continue With Confirmation: Are you sure you confirm all entries?, with Cancel and Yes.',
      },
    },
    {
      id: 'ticked',
      part: 'Post the month',
      heading: 'Check who to post',
      text: 'Every resident with something to post starts checked. Clear a resident\'s box to leave them for later. The line under the list adds up what the checked residents will post.',
      media: {
        image: {
          src: '/help/credits/ticked.webp',
          width: 1280,
          height: 760,
        },
        alt: 'Six residents checked, Raman, Priya now Posted, and the line under the list: 6 residents ticked, 222 meals use a credit, $171.75 to bill.',
      },
    },
    {
      id: 'post',
      part: 'Post the month',
      heading: 'Post',
      text: 'Select Post (it names how many residents are checked). The window sums up what will happen: how many meals use a credit, what is billed to residents\' accounts, and any attendance refunds.\n\nPosted meals cannot be changed here afterwards, so use Review first for anyone whose month needs a change.',
      media: {
        image: {
          src: '/help/credits/post.webp',
          width: 708,
          height: 490,
        },
        clip: {
          src: '/help/credits/post.mp4',
          width: 708,
          height: 490,
          durationSec: 2,
        },
        alt: 'Post October 2026?: for 6 residents, 222 meals use a credit, $171.75 is billed to resident accounts, 1 resident gets an attendance refund ($84.00), with Cancel and Post.',
      },
    },
    {
      id: 'done',
      part: 'Post the month',
      heading: 'The month is posted',
      text: 'Select Post. A bar shows the residents as they are posted; keep the page open until it finishes. The result says how many were posted, and names anyone who needs another look.\n\nSelect OK. Every row now says Posted, and Already posted counts them all.',
      media: {
        image: {
          src: '/help/credits/done.webp',
          width: 942,
          height: 312,
        },
        clip: {
          src: '/help/credits/done.mp4',
          width: 942,
          height: 312,
          durationSec: 3,
        },
        alt: 'October 2026 posted: Posted 6 residents, with OK.',
      },
    },
  ],
  tips: [
    'Post a month once it has ended and the last meals are in. The current month is not offered: it is still being charged.',
    'Review first when you know a resident\'s month needs a change, for example a meal that should not use a credit. Everyone else can go with Post.',
    'Posting twice does no harm: a resident already posted shows Posted and is left out.',
  ],
  notes: [
    'Credits are switched on for each facility in Facility Settings, Credits / Costs, Use Credits. With them off, residents are not charged for meals and there is nothing to post.',
    'Looking at the Credits page needs the Credits area on your role; Review and Post need Manage Credits. Changing Facility Settings needs the Facility area. Ask your administrator.',
    'Posted meals cannot be changed on this page afterwards. A resident\'s own charges and credits are on their record, under Credits.',
    'A facility can call credits something else (Meal Credits, for example). The page then uses that name.',
  ],
  related: [
    'selective-touch',
    'selective-touch-setup',
    'facility-setup',
    'discharge-readmit',
    'roles-and-access',
  ],
  recorded: '2026-11-03',
};

export default article;
